/**
 * Parse custom-workflow steps for the Schedule Workflow dialog and build per-run
 * delay overrides. These edits apply only to the scheduled run; they do not save
 * back to workflow_step_definitions.
 */

export type WorkflowScheduleStep = {
  action: string;
  label: string;
  id: string;
  options?: Record<string, string>;
};

export type WorkflowStepDelayDraft = {
  schedule_delay_enabled: string;
  schedule_delay_amount: string;
  schedule_delay_unit: string;
  schedule_delay_reason: string;
};

export type WorkflowStepDelayOverride = {
  index: number;
  id?: string;
  action?: string;
  schedule_delay_enabled: string;
  schedule_delay_amount?: string;
  schedule_delay_unit?: string;
  schedule_delay_reason?: string;
};

const DELAY_UNITS = new Set(["seconds", "minutes", "hours", "days"]);

const ACTION_LABELS: Record<string, string> = {
  create_user: "Create user",
  m365_create_new_user: "Create user",
  m365_reset_password: "Reset password",
  m365_update_contact_info: "Set contact info",
  m365_update_organizational_info: "Set org info",
  m365_update_personal_information: "Set personal info",
  m365_assign_license_to_user: "Assign license",
  m365_set_email_signature: "Set email signature",
  m365_add_user_to_group: "Assign groups",
  m365_set_manager: "Set manager",
  m365_add_user_to_team: "Add to Microsoft Team",
  add_to_sharepoint_site: "Assign SharePoint site",
  m365_grant_site_access: "Assign SharePoint site",
  compose_email: "Compose email",
  send_email: "Compose email",
  m365_send_email: "Compose email",
  itglue_create_password: "IT Glue: save user password",
  m365_block_unblock_user: "Block sign-in",
  m365_revoke_user_session: "Revoke sign-in sessions",
  m365_remove_license_from_user: "Remove Microsoft 365 licenses",
};

function normalizedWorkflowStepLabel(action: string, label: string): string {
  const configured = String(label || "").trim();
  const configuredLower = configured.toLowerCase();
  if (action === "m365_reset_password" && configuredLower === "set password") {
    return ACTION_LABELS[action];
  }
  if (
    action === "m365_remove_license_from_user" &&
    ["remove license", "remove licenses"].includes(configuredLower)
  ) {
    return ACTION_LABELS[action];
  }
  return configured;
}

export function isWorkflowScheduleDelayEnabled(value: unknown): boolean {
  return value === true || value === 1 || String(value ?? "").trim().toLowerCase() === "true";
}

function asOptionsRecord(raw: unknown): Record<string, string> | undefined {
  let options = raw;
  if (typeof options === "string") {
    try {
      options = JSON.parse(options);
    } catch {
      return undefined;
    }
  }
  if (!options || typeof options !== "object" || Array.isArray(options)) return undefined;
  const snapshot: Record<string, string> = {};
  Object.entries(options as Record<string, unknown>).forEach(([key, value]) => {
    if (value == null) return;
    snapshot[key] = typeof value === "string" ? value : String(value);
  });
  return snapshot;
}

/** Same runnable-step filter as Horizon parseWorkflowStepsJson. */
export function parseWorkflowScheduleSteps(raw: unknown): WorkflowScheduleStep[] {
  let parsed = raw;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed || "{}");
    } catch {
      return [];
    }
  }
  const steps = Array.isArray(parsed) ? parsed : (parsed as { steps?: unknown[] } | null)?.steps;
  if (!Array.isArray(steps)) return [];
  return steps
    .filter((step) => step && typeof step === "object" && String((step as { action?: string }).action || "").trim())
    .map((step) => {
      const row = step as { action?: string; label?: string; id?: string; options?: unknown };
      const action = String(row.action).trim();
      const snapshot: WorkflowScheduleStep = {
        action,
        label: normalizedWorkflowStepLabel(action, String(row.label || "")),
        id: String(row.id || "").trim(),
      };
      const options = asOptionsRecord(row.options);
      if (options) snapshot.options = options;
      return snapshot;
    });
}

export function delayDraftFromStepOptions(options?: Record<string, string>): WorkflowStepDelayDraft {
  const enabled = isWorkflowScheduleDelayEnabled(options?.schedule_delay_enabled);
  return {
    schedule_delay_enabled: enabled ? "true" : "false",
    schedule_delay_amount: String(options?.schedule_delay_amount || "1"),
    schedule_delay_unit: String(options?.schedule_delay_unit || "hours"),
    schedule_delay_reason: String(options?.schedule_delay_reason || ""),
  };
}

export function workflowScheduleStepTitle(step: WorkflowScheduleStep, index: number): string {
  const label = String(step.label || "").trim();
  if (label && !/^[a-z0-9_]+$/i.test(label)) return `${index + 1}. ${label}`;
  const action = String(step.action || "");
  const mapped = ACTION_LABELS[action];
  if (mapped) return `${index + 1}. ${mapped}`;
  const spaced = action.replace(/^m365_/, "").replace(/_/g, " ").trim();
  const pretty = spaced
    ? spaced.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    : "Step";
  return `${index + 1}. ${pretty}`;
}

export function workflowScheduleDelaySummary(draft: WorkflowStepDelayDraft): string | null {
  if (!isWorkflowScheduleDelayEnabled(draft.schedule_delay_enabled)) return null;
  const amount = String(draft.schedule_delay_amount || "").trim();
  const unit = String(draft.schedule_delay_unit || "hours").trim();
  if (!amount) return "Delay";
  const unitText = amount === "1" && unit.endsWith("s") ? unit.slice(0, -1) : unit;
  return `Delay: ${amount} ${unitText}`;
}

export function validateWorkflowStepDelayDrafts(drafts: WorkflowStepDelayDraft[]): string | null {
  for (let index = 0; index < drafts.length; index += 1) {
    const draft = drafts[index];
    if (!isWorkflowScheduleDelayEnabled(draft.schedule_delay_enabled)) continue;
    const amount = Number(String(draft.schedule_delay_amount || "").trim());
    if (!Number.isFinite(amount) || amount <= 0) {
      return `Step ${index + 1} delay must be a positive number`;
    }
    const unit = String(draft.schedule_delay_unit || "").trim().toLowerCase();
    if (!DELAY_UNITS.has(unit)) {
      return `Step ${index + 1} delay unit must be seconds, minutes, hours, or days`;
    }
  }
  return null;
}

export function buildWorkflowStepDelayOverrides(
  steps: WorkflowScheduleStep[],
  drafts: WorkflowStepDelayDraft[]
): WorkflowStepDelayOverride[] {
  return steps.map((step, index) => {
    const draft = drafts[index] || delayDraftFromStepOptions(step.options);
    const override: WorkflowStepDelayOverride = {
      index,
      schedule_delay_enabled: isWorkflowScheduleDelayEnabled(draft.schedule_delay_enabled) ? "true" : "false",
    };
    if (step.id) override.id = step.id;
    if (step.action) override.action = step.action;
    if (override.schedule_delay_enabled === "true") {
      override.schedule_delay_amount = String(draft.schedule_delay_amount || "1");
      override.schedule_delay_unit = String(draft.schedule_delay_unit || "hours");
      const reason = String(draft.schedule_delay_reason || "").trim();
      if (reason) override.schedule_delay_reason = reason;
    }
    return override;
  });
}

/**
 * Apply per-run delay drafts onto structured-workflow steps for Play-now.
 * Only delay keys change; other step options stay as stored.
 */
export function applyWorkflowStepDelayOverridesToSteps<
  T extends { id?: string; action?: string; options?: Record<string, string> },
>(steps: T[], overrides: WorkflowStepDelayOverride[]): T[] {
  const next = (Array.isArray(steps) ? steps : []).map((step) => ({
    ...step,
    ...(step.options ? { options: { ...step.options } } : {}),
  }));
  if (!Array.isArray(overrides) || overrides.length === 0) return next;
  overrides.forEach((override) => {
    const index = Number(override.index);
    if (!Number.isInteger(index) || index < 0 || index >= next.length) return;
    const step = next[index];
    const options = { ...(step.options || {}) };
    if (!isWorkflowScheduleDelayEnabled(override.schedule_delay_enabled)) {
      delete options.schedule_delay_enabled;
      delete options.schedule_delay_amount;
      delete options.schedule_delay_unit;
      delete options.schedule_delay_reason;
    } else {
      options.schedule_delay_enabled = "true";
      options.schedule_delay_amount = String(override.schedule_delay_amount || "1");
      options.schedule_delay_unit = String(override.schedule_delay_unit || "hours");
      const reason = String(override.schedule_delay_reason || "").trim();
      if (reason) options.schedule_delay_reason = reason;
      else delete options.schedule_delay_reason;
    }
    if (Object.keys(options).length > 0) step.options = options;
    else delete step.options;
  });
  return next;
}
